import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { OAuthGrant } from '../types/oauth';

interface UseOrgOAuthGrantsResult {
  grants: OAuthGrant[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  revokeGrant: (grantId: string) => Promise<void>;
}

/** Connected apps (OAuth grants) for an organization. Admins get everyone's; members get their own. */
export function useOrgOAuthGrants(organizationId?: string | null): UseOrgOAuthGrantsResult {
  const api = useAuthenticatedApi();

  const [grants, setGrants] = useState<OAuthGrant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGrants = useCallback(async () => {
    if (!organizationId) {
      setGrants([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setGrants(await api.get<OAuthGrant[]>(apiEndpoints.orgOAuthGrants.list(organizationId)));
    } catch (err: any) {
      console.error('Failed to load connected apps:', err);
      setGrants([]);
      setError(err?.message || 'Failed to load connected apps');
    } finally {
      setIsLoading(false);
    }
  }, [api, organizationId]);

  const revokeGrant = useCallback(
    async (grantId: string): Promise<void> => {
      if (!organizationId) {
        throw new Error('No organization selected');
      }
      await api.delete(apiEndpoints.orgOAuthGrants.revoke(organizationId, grantId));
      setGrants((prev) => prev.filter((g) => g.id !== grantId));
    },
    [api, organizationId]
  );

  useEffect(() => {
    void loadGrants();
  }, [loadGrants]);

  return { grants, isLoading, error, reload: loadGrants, revokeGrant };
}
